// https://medium.com/@alfianlosari/graphql-cursor-infinite-scroll-pagination-with-@apollo/client/react/components-client-and-github-api-fafbc510b667
import { useQuery } from '@apollo/client';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import Image from 'components/Image';
import PropTypes from 'prop-types';
import React, { useEffect, useState, Fragment } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { GET_POSTS, GET_PRIVATE_POSTS } from '../../helpers/graphql/posts';
import { getUser } from '../../helpers/token';
import communityImg from '../../images/community.svg';
import Link from '@mui/material/Link';
import FirstPostCard from '../Dashboard/FirstPostCard';
import GridPostCard from './GridPostCard';
import PostListItem from './PostListItem';

const StoriesCarousel = dynamic(() => import('../Stories/StoriesCarousel'), {
  ssr: false,
  loading: () => <div className="mt-1 w-100 mb-3" style={{ height: 200 }} />,
});

const PostGrid = props => {
  const defaultLimit = props.query ? props.query.limit : 0;

  const [hasMore, setHasMore] = useState(true);
  const [postsLength, setPostsLength] = useState(defaultLimit);

  // When switching between different props, e.g. feed/featured/created/ the
  // count needs to be reset
  useEffect(() => {
    setPostsLength(defaultLimit);
  }, []);

  const { data, loading, error, fetchMore } = useQuery(
    props.usePrivatePosts ? GET_PRIVATE_POSTS : GET_POSTS,
    {
      variables: props.query,
      ssr: false,
    },
  );

  const loader = (
    <>
      {((props.poststyle === 'comment' || props.poststyle === 'list') && (
        <Grid container spacing={0} alignItems="center" justifyContent="center">
          <Grid item lg={12} md={12} sm={12} xs={12} key="loader">
            <div className="p-5 text-center">
              <CircularProgress />
            </div>
          </Grid>
        </Grid>
      )) || (
        <Grid container spacing={0} alignItems="center" justifyContent="center">
          <Grid
            item
            lg={props.grid.lg}
            md={props.grid.md}
            sm={props.grid.sm}
            xs={props.grid.xs}
            key="loader"
            className="pb-2"
          >
            <GridPostCard cardHeight={props.cardHeight} />
          </Grid>
        </Grid>
      )}
    </>
  );

  if (loading) {
    return loader;
  }
  if (error) {
    return (
      <Card className="m-5 text-center" key="noposts">
        <CardContent>{error && 'Network Error. Are you online?'}</CardContent>
      </Card>
    );
  }
  const dataposts = props.usePrivatePosts ? data.privatePosts : data.posts;
  if (!data || dataposts === null) return <></>;
  if (dataposts.length < defaultLimit && hasMore) setHasMore(false);
  return (
    <InfiniteScroll
      loadMore={() => {
        if (postsLength === dataposts.length) {
          if (fetchMore)
            fetchMore({
              variables: {
                offset: dataposts ? dataposts.length : 0,
                limit: defaultLimit,
              },
              updateQuery: (prev, { fetchMoreResult }) => {
                if (
                  ((props.usePrivatePosts &&
                    fetchMoreResult.privatePosts.length) ||
                    fetchMoreResult.posts.length) < defaultLimit
                ) {
                  setHasMore(false);
                }
                if (!fetchMoreResult) return prev;
                return Object.assign(
                  {},
                  prev,
                  props.usePrivatePosts
                    ? {
                        privatePosts: [
                          ...prev.privatePosts,
                          ...fetchMoreResult.privatePosts,
                        ],
                      }
                    : {
                        posts: [...prev.posts, ...fetchMoreResult.posts],
                      },
                );
              },
            });
          setPostsLength(postsLength + defaultLimit);
        } else if (postsLength !== dataposts.length + defaultLimit) {
          // When switching between different props, e.g.
          // feed/featured/created/ and switching back, this fix is
          // needed.
          // There should be a better way though..
          setPostsLength(dataposts.length);
        }
      }}
      hasMore={hasMore}
      threshold={1200}
      loader={loader}
    >
      <Grid container spacing={0} alignItems="center" justifyContent="center">
        {dataposts &&
          dataposts.length > 0 &&
          dataposts.map((post, i) => {
            let card = <></>;
            if (props.poststyle === 'list') {
              card = (
                <PostListItem
                  post={post}
                  id={`${post._id}`}
                  mode="edit"
                  warnWhenHidden={props.warnWhenHidden && post.blacklist}
                />
              );
            } else {
              card = (
                <GridPostCard
                  cardHeight={props.cardHeight}
                  post={post}
                  hideSaveBtn={props.grid.lg < 8}
                />
              );
            }
            return (
              <Fragment key={post._id}>
                <Grid
                  item
                  lg={props.grid.lg}
                  md={props.grid.md}
                  sm={props.grid.sm}
                  xs={props.grid.xs}
                >
                  {card}
                </Grid>
                {props.active === 'featured' && i === 0 ? (
                  <StoriesCarousel />
                ) : (
                  <></>
                )}
              </Fragment>
            );
          })}
        {dataposts &&
          dataposts.length === 0 &&
          (props.active === 'dashboard' ? (
            <div className="container d-flex justify-content-center mt-5 mb-5">
              <div style={{ maxWidth: '800px' }}>
                <FirstPostCard />
              </div>
            </div>
          ) : (
            <Card className="m-5" key="noposts">
              <CardContent>
                {(props.active === 'communities' && (
                  <>
                    <Typography variant="h4" className="pt-1 pb-2 text-center">
                      Follow communities to get your personalized feed
                    </Typography>
                    <Image
                      className="img-fluid"
                      src={communityImg}
                      alt="community"
                    />
                  </>
                )) ||
                  (props.active === 'feed' && (
                    <>
                      <Typography
                        variant="h4"
                        className="pt-1 pb-2 text-center"
                      >
                        Start following your favorite authors to fill your feed!
                      </Typography>
                      <Image
                        className="img-fluid"
                        src={communityImg}
                        alt="community"
                      />
                    </>
                  )) ||
                  (props.active === 'blog' &&
                    `${
                      props.query.author === getUser()
                        ? `You haven't`
                        : `${props.query.author} hasn't`
                    } started blogging yet.`) ||
                  (['new', 'taking Off', 'discover', 'featured'].indexOf(
                    props.active,
                  ) === -1 && (
                    <>
                      <span>
                        {(props.active === 'destination' &&
                          'There are no posts yet about this destination.') ||
                          (props.active === 'topic' &&
                            'No posts found about this topic.') ||
                          (props.active === 'bookmarks' &&
                            `You don't have any bookmarks yet.`) ||
                          `There's nothing here yet.`}
                      </span>
                      {getUser() &&
                        (props.active === 'destination' ||
                          props.active === 'topic') && (
                          <>
                            {' '}
                            <Link href="/dashboard/publish">
                              Create one now!
                            </Link>
                          </>
                        )}
                    </>
                  )) ||
                  'No posts found. This could be a connection problem.'}
              </CardContent>
            </Card>
          ))}
      </Grid>
    </InfiniteScroll>
  );
};

PostGrid.defaultProps = {
  cardHeight: undefined,
};

PostGrid.propTypes = {
  query: PropTypes.objectOf(PropTypes.any).isRequired,
  cardHeight: PropTypes.number,
  poststyle: PropTypes.string.isRequired,
  grid: PropTypes.objectOf(PropTypes.number).isRequired,
};

export default PostGrid;
